import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import * as _ from "lodash";
import {VisitPlanAccountModel} from "../../../models/visit-plan-account.model";
import {FieldValueModel} from "../../../models/field-value.model";
import {VisitPlanCellModel} from "../../../models/visit-plan-cell.model";
import {VisitPlanCallModel} from "../../../models/visit-plan-call.model";
import {isUndefined} from "lodash";
import {VisitScheduleDayModel} from "../../../models/visit-schedule-day.model";
import {
  VisitPlanAccountAnalysisModel,
  VisitPlanAccountSlotAnalysisModel
} from "../../../models/visit-plan-account-analysis.model";

require('./visit-plan-accounts-table-row.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-accounts-table-row',
  templateUrl: './visit-plan-accounts-table-row.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanAccountsTableRowComponent implements OnInit, OnChanges {

  @Input() account: VisitPlanAccountModel;
  @Input() from: number;
  @Input() to: number;
  @Input() index: number;
  @Input() visitPlan: VisitPlanModel;
  @Input() programTypeAliasMap: { [key: string]: string };
  @Input() totalsColSize: number;
  @Input() visitScheduleDays: VisitScheduleDayModel[];
  @Input() visitPlanAccountAnalysis: VisitPlanAccountAnalysisModel;
  @Input() dayColWidth: number;
  @Input() tableFilterColumns: { isDisplayDeliveryEtd: boolean, isDisplayDeliveryUnits: boolean, isDisplayCallTypes: boolean, isDisplayServiceTime: boolean, isDisplayDeliveryDays: boolean};
  @Output() onDeleteCalls = new EventEmitter<{ deletedCallIds: string[], cellId: string }>();
  @Output() onUpdateVisitCellTracker = new EventEmitter<VisitPlanCellModel>;
  @Output() onUpdateCallsTimeWindow = new EventEmitter<{
    list: { callId: string, notBefore: number, notAfter: number }[],
    cellId: string
  }>();
  @Output() onAddCall = new EventEmitter<{ cellId: string, date: number }>();
  @Output() onAdvanceEditCall = new EventEmitter<{
    cellId: string,
    call: VisitPlanCallModel,
    visitPlanCell: VisitPlanCellModel
  }>();
  @Output() onAssignCallsToResource = new EventEmitter<{
    callIds: string[],
    resourceId: string,
    cellId: string,
    accountId: string,
    dateRange: { from: number, to: number }
  }>();
  @Output() onUnAssignCalls = new EventEmitter<[]>();
  @Output() onLockAccountChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onLockCallChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onRemoveDeliveries = new EventEmitter<{
    accountId: string,
    accountName: string,
    deliveriesCount: number,
    from: number,
    to: number
  }>();
  @Output() onOpenAccountInfo = new EventEmitter<{account: VisitPlanAccountModel}>();


  public visitSlots: VisitPlanCellModel[] = [];
  public isHover: boolean = false;
  public totals: {
    deliveryUnitsCount: number;
    deliveryDaysCount: number;
    merchandiseUnitsCount: number;
    merchandiseCount: number;
    ordersCount: number;
    salesCount: number;
    serviceTimeTotal: number;
    // Initial Values
    merchandiseInitialCount?: number;
    ordersInitialCount?: number;
    salesInitialCount?: number;
    serviceTimeInitialTotal?: number;
    deliveryUnitsInitialCount?: number;
    deliveryDaysInitialCount?: number;
  };

  private weekDeliveriesCount: number = 0;

  public notesList: string[];
  public visitPlanCellTrackerMap: { [key: string]: VisitPlanCellModel };
  public visitPlanAccountCellAnalysisMap: { [key: string]: VisitPlanAccountSlotAnalysisModel };

  constructor() {
  }

  ngOnInit() {
    this.initData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.account && !changes.account.isFirstChange()) {
      this.initData();
    }

    if (changes.visitPlanAccountAnalysis) {
      this.visitPlanAccountCellAnalysisMap = {}
      this.visitPlanAccountAnalysis?.slots?.forEach((slot) => {
        this.visitPlanAccountCellAnalysisMap[slot.slotId] = slot;
      })
    }
  }

  private initData() {
    this.totals = {
      merchandiseCount: 0,
      ordersCount: 0,
      salesCount: 0,
      serviceTimeTotal: 0,
      deliveryUnitsCount: 0,
      deliveryDaysCount: 0,
      merchandiseUnitsCount: 0,
      merchandiseInitialCount: 0,
      ordersInitialCount: 0,
      salesInitialCount: 0,
      serviceTimeInitialTotal: 0,
      deliveryUnitsInitialCount: 0,
      deliveryDaysInitialCount: 0
    };

    this.visitPlanCellTrackerMap = {};

    this.account.visitSlotTrackers?.forEach(tracker => {
      this.visitPlanCellTrackerMap[tracker.id] = tracker.entity;
    })
    this.notesList = [];

    this.account.visitSlots.forEach(cell => {
      // if tracker exists for this cell → override with tracker.entity
      const tracker = this.account.visitSlotTrackers?.find(
        t => t.id === cell.id
      );
      const effectiveCell = tracker ? tracker.entity : cell;

      if (!!effectiveCell.deliveryDetails && !!this._getFieldValue(effectiveCell.deliveryDetails.isDeliveryDay)) {
        this.totals.deliveryUnitsCount += this._getFieldValue(effectiveCell.deliveryDetails.deliveryUnits) || 0;
      }

      if (cell.initialTotals?.programTypes) {
        _.forOwn(cell.initialTotals.programTypes, (value, key) => {
          if (this.programTypeAliasMap[key] === 'M') {
            this.totals.merchandiseInitialCount += value;
          }
          if (this.programTypeAliasMap[key] === 'O') {
            this.totals.ordersInitialCount += value;
          }
          if (this.programTypeAliasMap[key] === 'S') {
            this.totals.salesInitialCount += value;
          }
        })
      }

      if (cell.totals?.deliveryDays) {
        this.totals.deliveryDaysCount += cell.totals.deliveryDays;
      }

      if (cell.initialTotals?.deliveryDays) {
        this.totals.deliveryDaysInitialCount += cell.initialTotals.deliveryDays;
      }

      if (cell.initialTotals?.deliveryUnits) {
        this.totals.deliveryUnitsInitialCount += cell.initialTotals.deliveryUnits;
      }

      if (cell.initialTotals?.destinationServiceTime) {
        this.totals.serviceTimeInitialTotal += cell.initialTotals.destinationServiceTime;
      }

      effectiveCell.calls?.forEach(call => {
        if (this.programTypeAliasMap[this._getFieldValue(call.programType)] === 'M') {
          this.totals.merchandiseCount += 1;
        }
        if (this.programTypeAliasMap[this._getFieldValue(call.programType)] === 'O') {
          this.totals.ordersCount += 1;
        }
        if (this.programTypeAliasMap[this._getFieldValue(call.programType)] === 'S') {
          this.totals.salesCount += 1;
        }

        if (!!call.origin) {
          this.totals.serviceTimeTotal += this._getFieldValue(call.origin.serviceTime)?.generatedTotalTime;

          if (!!call.origin.notes?.length) {
            this.notesList = this.notesList.concat(call.origin.notes);
          }
        }

        if (!!call.destination) {
          this.totals.serviceTimeTotal += this._getFieldValue(call.destination.serviceTime)?.generatedTotalTime || 0;

          if (!!call.units) {
            this.totals.merchandiseUnitsCount += this._getFieldValue(call.units) || 0;
          }

          if (!!call.destination.notes?.length) {
            this.notesList = this.notesList.concat(call.destination.notes);
          }
        }
      });
    });

    this.visitSlots = this.account.visitSlots.filter((v) => {
      return this._isDateInRange(v.date, this.from, this.to);
    })

    this.weekDeliveriesCount = this.visitSlots.reduce((sum, cell) => {
      return sum + (cell.totals?.deliveryDays || 0);
    }, 0);
  }

  onRemoveAccountDeliveries() {
    this.onRemoveDeliveries.emit({
      accountId: this.account.accountId,
      accountName: this.account.accountReference.name,
      deliveriesCount: this.weekDeliveriesCount,
      from: this.from,
      to: this.to
    });
  }

  private _getFieldValue<T>(field: FieldValueModel<T>): T | undefined {
    if (!field) {
      return undefined;
    } else if (isUndefined(field.adjusted?.value)) {
      return field.initial?.value
    } else {
      return field.adjusted?.value
    }
  }

  private _isDateInRange(
    targetTs: number,
    startTs: number,
    endTs: number
  ): boolean {
    const normalize = (ts: number) => {
      const d = new Date(ts);
      return new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate()
      ).getTime();
    };

    const target = normalize(targetTs);
    const start = normalize(startTs);
    const end = normalize(endTs);

    return target >= start && target <= end;
  }

}
